import { HttpClient } from "@angular/common/http";
import { Injectable, computed, inject, signal } from "@angular/core";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { DocumentRequest } from "./request";
import { DocumentType } from "@main/file-storage/document-type";
import { DocumentUploaderService } from "../document-uploader.service";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { filter } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DocumentUploadRequestSentEventData, DocumentUploadRequestSentEventName } from "../events";

declare type Step = "init" | "requests" | "upload" | "refresh";

declare type Status = "idle" | "loading" | "success" | "error";

declare type State = {
    episodeId?: string;
    step: Step;
    status: Status;
    documentRequestsCount: number;
    documentRequests: DocumentRequest[];
};

declare type UploadInfo = {
    requestId: string;
    documentType: DocumentType;
    uploadDateTime: Date;
    files: File[];
};

@Injectable()
export class DocumentsRequestedRibbonService extends BaseDataService {
    readonly url = "api/document-upload/request";

    private readonly _defaultState: State = {
        step: "init",
        status: "idle",
        documentRequestsCount: 0,
        documentRequests: []
    };
    private readonly state$$ = signal<State>(this._defaultState);
    private readonly uploaderService = inject(DocumentUploaderService);
    private readonly broadcastService = inject(BroadcastService);

    constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
        super(http, handlersFactory);
        this.broadcastService
            .on<DocumentUploadRequestSentEventData>(DocumentUploadRequestSentEventName)
            .pipe(
                takeUntilDestroyed(),
                filter(({ episodeId }) => !!episodeId && episodeId === this.state$$().episodeId)
            )
            .subscribe(_ => this.reload());
    }

    readonly step$$ = computed(() => this.state$$().step);
    readonly status$$ = computed(() => this.state$$().status);
    readonly documentRequests$$ = computed(() => this.state$$().documentRequests);
    readonly documentRequestsCount$$ = computed(() => this.state$$().documentRequestsCount);
    readonly hasRequests$$ = computed(() => this.documentRequestsCount$$() > 0);
    readonly uploadError$$ = computed(() => this.step$$() === "upload" && this.status$$() === "error");
    readonly loadingRequests$$ = computed(() => this.step$$() === "requests" && this.status$$() === "loading");
    readonly refreshing$$ = computed(() => this.step$$() === "refresh" && this.status$$() === "loading");

    async initialize(episodeId: string) {
        this.setState({ ...this.state$$(), status: "loading", episodeId });

        try {
            const response = await this.handleRequest(this.http.get<{ count: number }>(`${this.url}/me/count`, { params: { episodeId } }));
            this.setState({ ...this.state$$(), status: "success", documentRequestsCount: response.count });
        } catch (error) {
            this.setState({ ...this.state$$(), status: "error", documentRequestsCount: 0 });
        }
    }

    async getDocumentRequests() {
        // only request if we've just initialized or refreshed
        if (this.step$$() !== "init" && this.step$$() !== "refresh") {
            return;
        }

        this.setState({ ...this.state$$(), status: "loading", step: "requests" });

        try {
            const requests = await this.handleRequest(
                this.http.get<{ requests: DocumentRequest[] }>(`${this.url}/me`, { params: { episodeId: this.state$$().episodeId! } })
            );
            this.setState({ ...this.state$$(), status: "success", documentRequests: requests.requests });
        } catch (error) {
            this.setState({ ...this.state$$(), status: "error", documentRequests: [] });
        }
    }

    async upload({ requestId, documentType: docType, uploadDateTime, files }: UploadInfo) {
        this.setState({ ...this.state$$(), status: "loading", step: "upload" });

        try {
            const fileDetails = await this.uploaderService.uploadDocument(requestId, docType, uploadDateTime, files);
            this.setState({ ...this.state$$(), status: "success" });
            this.reload();

            return fileDetails;
        } catch {
            this.setState({ ...this.state$$(), status: "error" });
            return [];
        }
    }

    private reload() {
        this.setState({ ...this._defaultState, episodeId: this.state$$().episodeId, step: "refresh" });
        this.initialize(this.state$$().episodeId!);
    }

    private setState(state?: State) {
        state ??= this._defaultState;
        this.state$$.update(_ => ({ ...state }));
    }
}
